/**
 * FichasOnline — Gestão de fichas de anamnese enviadas por link.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { fichasService, patientsService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../Modal/ConfirmModal';
import { Ico, emo } from '../ui/Ico';

const BRAND  = '#D4A574';
const BRAND2 = '#b8895c';

const STATUS_CFG = {
  pendente:  { label: '⏳ Aguardando',  bg: '#f8f1e2', color: '#8a5f1a' },
  preenchida:{ label: '✅ Preenchida pelo Paciente',  bg: '#edf4ee', color: '#2f5236' },
  importada: { label: '📥 Importada',  bg: '#edf1f6', color: '#4f6a86' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Visualização full-screen da ficha ───────────────────────────────────────

const ANALISE_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8888';

function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob), now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age > 0 ? age : null;
}

// Renderiza markdown simples (###, **, -, listas numeradas) sem dependências externas
function MarkdownText({ text }) {
  if (!text) return null;

  const inlineBold = (str) => {
    const parts = String(str).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={i} style={{ fontWeight: 600 }}>{p.slice(2, -2)}</strong>
        : p
    );
  };

  return (
    <div>
      {text.split('\n').map((line, i) => {
        if (/^###\s/.test(line))
          return <h4 key={i} style={{ margin: '1.1rem 0 0.3rem', fontSize: '0.92rem', fontWeight: 700, color: '#2f2a26' }}>{line.slice(4)}</h4>;
        if (/^##\s/.test(line))
          return <h3 key={i} style={{ margin: '1.2rem 0 0.35rem', fontSize: '1rem', fontWeight: 700, color: '#2f2a26' }}>{line.slice(3)}</h3>;
        if (/^#\s/.test(line))
          return <h2 key={i} style={{ margin: '1.3rem 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: '#2f2a26' }}>{line.slice(2)}</h2>;
        if (/^[-•]\s/.test(line))
          return (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.3rem', alignItems: 'flex-start' }}>
              <span style={{ color: BRAND, fontWeight: 600, lineHeight: 1.6, flexShrink: 0 }}>▸</span>
              <span style={{ fontSize: '0.87rem', color: '#2f2a26', lineHeight: 1.65 }}>{inlineBold(line.slice(2))}</span>
            </div>
          );
        if (/^\d+\.\s/.test(line))
          return (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.3rem', alignItems: 'flex-start' }}>
              <span style={{ color: BRAND2, fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.6, flexShrink: 0, minWidth: '1.2rem' }}>{emo(line.match(/^\d+/)[0])}.</span>
              <span style={{ fontSize: '0.87rem', color: '#2f2a26', lineHeight: 1.65 }}>{inlineBold(line.replace(/^\d+\.\s/, ''))}</span>
            </div>
          );
        if (line.trim() === '')
          return <div key={i} style={{ height: '0.55rem' }} />;
        return <p key={i} style={{ margin: '0 0 0.3rem', fontSize: '0.87rem', color: '#2f2a26', lineHeight: 1.65 }}>{inlineBold(line)}</p>;
      })}
    </div>
  );
}

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  const display = Array.isArray(value) ? value.join(', ') : String(value);
  if (!display.trim()) return null;
  return (
    <div style={{ padding: '0.55rem 0', borderBottom: '1px solid #ebe3da' }}>
      <p style={{ margin: '0 0 0.15rem', fontSize: '0.7rem', fontWeight: 600, color: '#d4a574', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{emo(label)}</p>
      <p style={{ margin: 0, fontSize: '0.88rem', color: '#2f2a26', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{display}</p>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <p style={{ margin: '1.25rem 0 0.6rem', fontSize: '0.75rem', fontWeight: 700, color: '#5a3e2b', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      {children}
    </p>
  );
}

function ViewModal({ ficha, onClose, onNavigate }) {
  const dados = ficha.dados_anamnese
    ? (() => { try { return JSON.parse(typeof ficha.dados_anamnese === 'string' ? ficha.dados_anamnese : JSON.stringify(ficha.dados_anamnese)); } catch { return null; } })()
    : null;

  const [tab, setTab]               = useState('dados');
  const [analise, setAnalise]       = useState(null);
  const [analiseLoad, setAnaliseLoad] = useState(true);
  const [analisando, setAnalisando]  = useState(false);
  const [analiseErr, setAnaliseErr]  = useState('');
  const [criarPlanoLoading, setCriarPlanoLoading] = useState(false);
  const [criarPlanoErr, setCriarPlanoErr]         = useState('');

  const tokenHex = (ficha.token || '').replace(/[^a-f0-9]/g, '');
  const age      = calcAge(dados?.data_nascimento);
  const statusCfg = STATUS_CFG[ficha.status] || STATUS_CFG.pendente;

  useEffect(() => {
    if (!ficha.token) { setAnaliseLoad(false); return; }
    fichasService.getAnalise(ficha.token)
      .then(res => { if (res.success) setAnalise(res.data); })
      .catch(() => {})
      .finally(() => setAnaliseLoad(false));
  }, [ficha.token]);

  const handleAnalisar = async () => {
    setAnalisando(true);
    setAnaliseErr('');
    try {
      const res = await fichasService.analise(ficha.token);
      if (!res.success) throw new Error(res.error || 'Erro ao gerar análise');
      if (res.data?.sugestao) {
        setAnalise(prev => ({ ...prev, sugestao: res.data.sugestao }));
      } else {
        const r2 = await fichasService.getAnalise(ficha.token);
        if (r2.success) setAnalise(r2.data);
      }
    } catch (e) {
      setAnaliseErr(e.message || 'Erro ao gerar análise. Tente novamente.');
    } finally {
      setAnalisando(false);
    }
  };

  const handleCriarPlano = async () => {
    setCriarPlanoLoading(true);
    setCriarPlanoErr('');
    try {
      let patientId = null;

      // 1. Tentar importar (funciona para fichas 'preenchida'; 409 = já importada)
      try {
        const res = await fichasService.importar(ficha.token);
        if (res.success) patientId = res.data?.patient_id;
      } catch (importErr) {
        if (importErr.response?.status !== 409) throw importErr;
        patientId = importErr.response?.data?.data?.patient_id || null;
      }

      // 2. Verificar se o patient_id existente corresponde à paciente correta pelo nome
      const pRes = await patientsService.getAll();
      const allPatients = pRes.success ? (pRes.data || []) : [];
      const fichaName = (ficha.nome_paciente || '').toLowerCase().trim();

      if (patientId) {
        const linked = allPatients.find(p => String(p.id) === String(patientId));
        if (!linked || (linked.name || '').toLowerCase().trim() !== fichaName) {
          patientId = null; // patient_id aponta para outra pessoa — descartar
        }
      }

      // 3. Buscar pelo nome exato na lista de pacientes
      if (!patientId) {
        const match = allPatients.find(p => (p.name || '').toLowerCase().trim() === fichaName);
        patientId = match?.id || null;
      }

      // 4. Criar paciente automaticamente com dados da ficha
      if (!patientId) {
        const birthDate = dados?.data_nascimento || '2000-01-01';
        const genderRaw = (dados?.sexo || '').toLowerCase();
        const gender = genderRaw.includes('masc') || genderRaw.includes('homem') ? 'M' : 'F';
        const createRes = await patientsService.create({
          name:           ficha.nome_paciente,
          birth_date:     birthDate,
          gender:         gender,
          objective:      dados?.objetivo_principal || 'Consulta nutricional',
          phone:          ficha.whatsapp || '',
          whatsapp:       ficha.whatsapp || '',
          email:          ficha.email    || '',
          cidade:         dados?.cidade  || '',
          estado:         dados?.estado  || '',
          initial_weight: dados?.peso_atual || null,
          initial_height: dados?.altura     || null,
        });
        if (!createRes.success) throw new Error(createRes.error || 'Erro ao criar paciente');
        patientId = createRes.data?.id || createRes.data?.patient_id;
      }

      if (!patientId) throw new Error('Não foi possível identificar ou criar a paciente.');

      if (analise?.sugestao) {
        localStorage.setItem(`lev_ia_rascunho_${patientId}`, JSON.stringify({
          sugestao: analise.sugestao,
          token: ficha.token,
          nome_paciente: ficha.nome_paciente,
          ts: Date.now(),
        }));
      }
      onClose();
      if (onNavigate) onNavigate('plano-alimentar', patientId);
    } catch (e) {
      setCriarPlanoErr(e.message || 'Erro ao processar. Tente novamente.');
    } finally {
      setCriarPlanoLoading(false);
    }
  };

  const TABS = [
    { id: 'dados',   label: '📋 Dados da Paciente' },
    { id: 'habitos', label: '🥗 Hábitos Alimentares' },
    { id: 'saude',   label: '💪 Saúde & Estilo de Vida' },
    { id: 'exames',  label: `🔬 Exames & IA${analise?.exames?.length ? ` (${analise.exames.length})` : ''}` },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}>
      <div style={{ background: '#fbf8f5', borderRadius: '20px', width: '95vw', height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.4)', overflow: 'hidden' }}>

        {/* ── Barra superior ──────────────────────────────────────────────── */}
        <div style={{ background: '#ffffff', borderBottom: '1px solid #ebe3da', padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ padding: '0.45rem 0.9rem', background: '#f6f0ea', color: '#5a3e2b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            ← Voltar
          </button>
          <span style={{ padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: statusCfg.bg, color: statusCfg.color }}>
            {emo(statusCfg.label)}
          </span>
          {analise?.exames?.length > 0 && (
            <span style={{ padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: '#edf1f6', color: '#34495e' }}>
              <Ico e="📎" /> {analise.exames.length} exame{analise.exames.length > 1 ? 's' : ''}
            </span>
          )}
          {analise?.sugestao && (
            <span style={{ padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: '#edf1f6', color: '#7189a6' }}>
              <Ico e="🤖" sp />IA gerada
            </span>
          )}
          <div style={{ flex: 1 }} />
          {criarPlanoErr && (
            <span style={{ fontSize: '0.78rem', color: '#c46a60', fontWeight: 600 }}>{criarPlanoErr}</span>
          )}
          {(ficha.status === 'preenchida' || ficha.status === 'importada') && (
            <button onClick={handleCriarPlano} disabled={criarPlanoLoading}
              style={{ padding: '0.55rem 1.1rem', background: criarPlanoLoading ? '#b9d2bd' : 'linear-gradient(135deg,#46714f,#6f9a77)', color: '#ffffff', border: 'none', borderRadius: '9px', cursor: criarPlanoLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>
              {emo(criarPlanoLoading ? '⏳ Processando...' : '➕ Criar Plano Alimentar')}
            </button>
          )}
        </div>

        {/* ── Cabeçalho da paciente ────────────────────────────────────────── */}
        <div style={{ background: `linear-gradient(135deg, ${BRAND}18, #fff5ec60)`, borderBottom: '1px solid #ebe3da', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#ffffff', fontSize: '1.4rem', flexShrink: 0, boxShadow: `0 4px 12px ${BRAND}50` }}>
            {(ficha.nome_paciente || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.2rem', fontWeight: 700, color: '#2f2a26' }}>{ficha.nome_paciente}</h2>
            <p style={{ margin: 0, fontSize: '0.83rem', color: '#736b64', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {age && <span><Ico e="🎂" /> {age} anos</span>}
              {dados?.objetivo_principal && <span><Ico e="🎯" /> {dados.objetivo_principal}</span>}
              {dados?.cidade && <span><Ico e="📍" /> {[dados.cidade, dados.estado].filter(Boolean).join('/')}</span>}
              {dados?.peso_atual && <span><Ico e="⚖" /> {dados.peso_atual}kg{dados.altura ? ` · ${dados.altura}cm` : ''}</span>}
              {ficha.preenchido_em && <span><Ico e="📅" /> {fmtDate(ficha.preenchido_em)}</span>}
            </p>
          </div>
          {dados?.queixa_principal && (
            <div style={{ maxWidth: '280px', background: '#ffffff', borderRadius: '10px', padding: '0.6rem 0.9rem', border: '1px solid #ebe3da', flexShrink: 0 }}>
              <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase' }}>Queixa principal</p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#2f2a26', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {dados.queixa_principal}
              </p>
            </div>
          )}
        </div>

        {/* ── Abas ────────────────────────────────────────────────────────── */}
        <div style={{ background: '#ffffff', borderBottom: '1px solid #ebe3da', display: 'flex', gap: 0, flexShrink: 0, overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: '0.75rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer',
                fontWeight: tab === t.id ? 700 : 500, fontSize: '0.85rem',
                color: tab === t.id ? BRAND2 : '#736b64',
                borderBottom: tab === t.id ? `2.5px solid ${BRAND}` : '2.5px solid transparent',
                whiteSpace: 'nowrap', transition: 'all 0.15s',
              }}>
              {emo(t.label)}
            </button>
          ))}
        </div>

        {/* ── Conteúdo das abas ────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          {!dados ? (
            <p style={{ color: '#736b64', textAlign: 'center', padding: '3rem' }}>Ficha ainda não preenchida.</p>
          ) : (
            <>
              {/* ABA 1 — Dados da Paciente */}
              {tab === 'dados' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                  <div style={{ background: '#ffffff', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #ebe3da' }}>
                    <SectionHeading><Ico e="👤" sp />Identificação</SectionHeading>
                    <Row label="Nome completo" value={dados.nome_completo} />
                    <Row label="Data de nascimento" value={dados.data_nascimento} />
                    <Row label="Sexo" value={dados.sexo} />
                    <Row label="WhatsApp" value={dados.whatsapp} />
                    <Row label="E-mail" value={dados.email} />
                    <Row label="Cidade / Estado" value={[dados.cidade, dados.estado].filter(Boolean).join(' / ')} />
                    <Row label="Profissão" value={dados.profissao} />
                    <Row label="Como conheceu" value={dados.como_conheceu} />
                  </div>
                  <div style={{ background: '#ffffff', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #ebe3da' }}>
                    <SectionHeading><Ico e="🎯" sp />Objetivos</SectionHeading>
                    <Row label="Objetivo principal" value={dados.objetivo_principal} />
                    <Row label="Peso atual" value={dados.peso_atual && `${dados.peso_atual} kg`} />
                    <Row label="Peso desejado" value={dados.peso_desejado && `${dados.peso_desejado} kg`} />
                    <Row label="Altura" value={dados.altura && `${dados.altura} cm`} />
                    <Row label="Tratamento anterior" value={dados.tratamento_anterior === 'Sim' ? dados.tratamento_anterior_desc || 'Sim' : dados.tratamento_anterior} />
                    <SectionHeading><Ico e="💬" sp />Queixa & Expectativas</SectionHeading>
                    <Row label="Queixa principal" value={dados.queixa_principal} />
                    <Row label="Expectativas" value={dados.expectativas} />
                    <Row label="Observações" value={dados.algo_mais} />
                  </div>
                </div>
              )}

              {/* ABA 2 — Hábitos Alimentares */}
              {tab === 'habitos' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                  <div style={{ background: '#ffffff', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #ebe3da' }}>
                    <SectionHeading><Ico e="🍽" sp />Rotina Alimentar</SectionHeading>
                    <Row label="Refeições por dia" value={dados.num_refeicoes} />
                    <Row label="Água (L/dia)" value={dados.consumo_agua} />
                    <Row label="Consumo de álcool" value={dados.consumo_alcool} />
                    <Row label="Come fora de casa" value={dados.come_fora} />
                    <Row label="Preferência por doces" value={dados.pref_doces && `${dados.pref_doces}/5`} />
                    <Row label="Preferência por salgados" value={dados.pref_salgados && `${dados.pref_salgados}/5`} />
                    <Row label="Compulsão alimentar" value={dados.compulsao_alimentar} />
                    <Row label="Come por ansiedade" value={dados.come_por_ansiedade} />
                  </div>
                  <div style={{ background: '#ffffff', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #ebe3da' }}>
                    <SectionHeading><Ico e="✅" sp />Preferências & Restrições</SectionHeading>
                    <Row label="Alimentos que gosta" value={dados.alimentos_gosta} />
                    <Row label="Alimentos que evita" value={dados.alimentos_nao_gosta} />
                    <Row label="Restrições alimentares" value={dados.restricoes_alimentares_multi} />
                    <Row label="Alergias alimentares" value={dados.alergias_alimentares === 'Sim' ? dados.alergias_alimentares_desc : 'Não'} />
                    <Row label="Intolerâncias" value={dados.intolerancia_alimentar === 'Sim' ? dados.intolerancia_alimentar_desc : 'Não'} />
                    <Row label="Suplementos" value={dados.usa_suplementos === 'Sim' ? dados.suplementos_desc : 'Não'} />
                  </div>
                </div>
              )}

              {/* ABA 3 — Saúde & Estilo de Vida */}
              {tab === 'saude' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                  <div style={{ background: '#ffffff', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #ebe3da' }}>
                    <SectionHeading><Ico e="🏥" sp />Histórico de Saúde</SectionHeading>
                    <Row label="Doenças diagnosticadas" value={dados.doencas} />
                    <Row label="Cirurgias" value={dados.cirurgias === 'Sim' ? dados.cirurgias_desc : 'Não'} />
                    <Row label="Medicamentos" value={dados.medicamentos === 'Sim' ? dados.medicamentos_desc : 'Não'} />
                    <Row label="Histórico familiar" value={dados.historico_familiar} />
                    <SectionHeading>♀️ Saúde Feminina</SectionHeading>
                    <Row label="Ciclo menstrual" value={dados.ciclo_regular} />
                    <Row label="Sintomas de TPM" value={dados.tpm === 'Sim' ? dados.ciclo_sintomas || 'Sim' : dados.tpm} />
                    <Row label="Grávida / Amamentando" value={dados.gravida_amamentando} />
                    <SectionHeading><Ico e="🧪" sp />Exames Laboratoriais</SectionHeading>
                    <Row label="Exames recentes" value={dados.exames_recentes} />
                    <Row label="Alterações nos exames" value={dados.exames_alteracoes} />
                  </div>
                  <div style={{ background: '#ffffff', borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #ebe3da' }}>
                    <SectionHeading><Ico e="🏃" sp />Atividade Física</SectionHeading>
                    <Row label="Pratica atividade" value={dados.pratica_atividade} />
                    {dados.pratica_atividade === 'Sim' && <>
                      <Row label="Tipo" value={dados.tipo_atividade} />
                      <Row label="Frequência" value={dados.frequencia_atividade && `${dados.frequencia_atividade}x/semana`} />
                      <Row label="Duração" value={dados.duracao_atividade && `${dados.duracao_atividade} min`} />
                    </>}
                    <SectionHeading><Ico e="😴" sp />Sono & Bem-estar</SectionHeading>
                    <Row label="Qualidade do sono" value={dados.qualidade_sono} />
                    <Row label="Horas de sono" value={dados.horas_sono && `${dados.horas_sono}h/noite`} />
                    <Row label="Nível de estresse" value={dados.nivel_estresse && `${dados.nivel_estresse}/10`} />
                    <Row label="Período de trabalho" value={dados.periodo_trabalho} />
                    <Row label="Filhos" value={dados.tem_filhos === 'Sim' ? `Sim (${dados.qtd_filhos || '?'})` : 'Não'} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ABA 4 — Exames & IA (renderiza independente de dados) */}
          {tab === 'exames' && (
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              {/* Exames anexados */}
              {analiseLoad ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#a39b93' }}>Carregando exames...</div>
              ) : analise?.exames?.length > 0 ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ margin: '0 0 0.9rem', fontSize: '0.8rem', fontWeight: 700, color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <Ico e="📎" sp />Exames Anexados ({analise.exames.length})
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                    {analise.exames.map((ex, i) => {
                      const url = `${ANALISE_BASE}/uploads/exames/${tokenHex}/${ex.filename}`;
                      const isImage = ex.mime_type?.startsWith('image/');
                      return (
                        <div key={i} style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #edf1f6', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                          {isImage ? (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt={ex.original_name}
                                style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block', background: '#edf1f6' }}
                                onError={e => { e.target.style.display = 'none'; }} />
                            </a>
                          ) : (
                            <div style={{ height: '80px', background: '#edf1f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}><Ico e="📄" /></div>
                          )}
                          <div style={{ padding: '0.6rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ flex: 1, fontSize: '0.78rem', color: '#34495e', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ex.original_name}
                            </span>
                            <a href={url} target="_blank" rel="noopener noreferrer"
                              style={{ flexShrink: 0, fontSize: '0.75rem', color: '#b8c7d8', fontWeight: 600, textDecoration: 'none', background: '#edf1f6', padding: '0.2rem 0.5rem', borderRadius: '6px' }}>
                              ↓
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : !analiseLoad && (
                <div style={{ textAlign: 'center', padding: '1.5rem', background: '#fbf8f5', borderRadius: '12px', border: '1px dashed #b8c7d8', marginBottom: '1.5rem' }}>
                  <p style={{ margin: 0, color: '#7189a6', fontSize: '0.88rem' }}><Ico e="📎" sp />Nenhum exame anexado pela paciente</p>
                </div>
              )}

              {/* Análise IA */}
              {analise?.sugestao ? (
                <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1.5px solid #D4A57440' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: '#7189a6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      <Ico e="🤖" sp />Sugestão de Plano Alimentar — Gerada por IA
                    </p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(analise.sugestao); }}
                      style={{ padding: '0.3rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                      <Ico e="📋" sp />Copiar
                    </button>
                  </div>
                  <div style={{ borderTop: '1px solid #edf1f6', paddingTop: '1rem' }}>
                    <MarkdownText text={analise.sugestao} />
                  </div>
                  <div style={{ marginTop: '1rem', padding: '0.65rem 0.9rem', background: '#fbf8f5', borderRadius: '8px', border: '1px solid #D4A57430' }}>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#8a5f1a', fontStyle: 'italic' }}>
                      <Ico e="⚠" sp />Esta é uma sugestão gerada por IA para revisão da nutricionista — não é uma prescrição final. Use como base ao criar o Plano Alimentar da paciente.
                    </p>
                  </div>
                  {(ficha.status === 'preenchida' || ficha.status === 'importada') && (
                    <button onClick={handleCriarPlano} disabled={criarPlanoLoading}
                      style={{ marginTop: '1rem', width: '100%', padding: '0.8rem', background: criarPlanoLoading ? '#b9d2bd' : 'linear-gradient(135deg,#46714f,#6f9a77)', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: criarPlanoLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.95rem' }}>
                      {emo(criarPlanoLoading ? '⏳ Processando...' : '➕ Criar Plano Alimentar com IA')}
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ background: '#ffffff', borderRadius: '16px', padding: '2rem', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1.5px dashed #b8c7d8' }}>
                  <p style={{ fontSize: '2.5rem', margin: '0 0 0.75rem' }}><Ico e="🤖" /></p>
                  <p style={{ margin: '0 0 0.4rem', fontWeight: 600, color: '#7189a6', fontSize: '1rem' }}>
                    {analise?.exames?.length > 0
                      ? `${analise.exames.length} exame${analise.exames.length > 1 ? 's' : ''} pronto${analise.exames.length > 1 ? 's' : ''} para análise`
                      : 'Gerar análise da anamnese com IA'}
                  </p>
                  <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#736b64', maxWidth: '400px', marginInline: 'auto' }}>
                    O GPT-4o analisa os dados da ficha{analise?.exames?.length > 0 ? ', os exames anexados' : ''} e sugere um plano alimentar inicial personalizado para você revisar.
                  </p>
                  {analiseErr && (
                    <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#c46a60', fontWeight: 600 }}>{analiseErr}</p>
                  )}
                  {!analiseLoad && (
                    <button onClick={handleAnalisar} disabled={analisando}
                      style={{ padding: '0.8rem 2rem', background: analisando ? '#d9d0c6' : 'linear-gradient(135deg,#7189a6,#b8c7d8)', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: analisando ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '1rem', boxShadow: analisando ? 'none' : '0 4px 16px #a78bfa60' }}>
                      {emo(analisando ? '⏳ Analisando com IA...' : '🤖 Analisar com IA')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Gerar novo token ──────────────────────────────────────────────────

function NovoTokenModal({ onClose, onGenerated }) {
  const { showError } = useToast();
  const [form, setForm]     = useState({ nome_paciente: '', whatsapp: '', email: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome_paciente) { showError('Informe o nome da paciente'); return; }
    setLoading(true);
    try {
      const res = await fichasService.createToken(form);
      if (res.success) { onGenerated(res.data); onClose(); }
      else showError(res.error || 'Erro ao gerar token');
    } catch { showError('Erro de comunicação'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.05rem', fontWeight: 600 }}><Ico e="📋" sp />Gerar Link de Ficha</h3>
        <form onSubmit={handleSubmit}>
          {[
            { name: 'nome_paciente', label: 'Nome da paciente', required: true, placeholder: 'Nome completo' },
            { name: 'whatsapp', label: 'WhatsApp', placeholder: '(00) 00000-0000', type: 'tel' },
            { name: 'email', label: 'E-mail (opcional)', placeholder: 'email@exemplo.com', type: 'email' },
          ].map(f => (
            <div key={f.name} style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#5a3e2b', marginBottom: '0.3rem' }}>{emo(f.label)}{f.required && ' *'}</label>
              <input type={f.type || 'text'} required={f.required} placeholder={f.placeholder}
                value={form[f.name]} onChange={e => setForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                style={{ width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.65rem', background: '#f6f0ea', color: '#5a3e2b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
            <button type="submit" disabled={loading} style={{ flex: 1, padding: '0.65rem', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {emo(loading ? 'Gerando...' : '🔗 Gerar Link')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal: Link gerado ───────────────────────────────────────────────────────

function LinkModal({ result, onClose }) {
  const { showSuccess } = useToast();
  const copy = () => { navigator.clipboard.writeText(result.link); showSuccess('Link copiado!'); };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <p style={{ fontSize: '2.5rem', margin: '0 0 0.5rem' }}><Ico e="🔗" /></p>
          <h3 style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>Link gerado com sucesso!</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#736b64' }}>Envie este link para a paciente pelo WhatsApp</p>
        </div>
        <div style={{ background: '#fbf8f5', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', wordBreak: 'break-all', fontSize: '0.82rem', color: '#4a4440' }}>
          {result.link}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={copy} style={{ flex: 1, padding: '0.65rem', background: '#f6f0ea', color: '#5a3e2b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>
            <Ico e="📋" sp />Copiar Link
          </button>
          {result.wa_link && (
            <a href={result.wa_link} target="_blank" rel="noopener noreferrer"
              style={{ flex: 1, padding: '0.65rem', background: '#edf4ee', color: '#2f5236', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', textAlign: 'center', textDecoration: 'none' }}>
              <Ico e="💬" sp />Abrir WhatsApp
            </a>
          )}
          <button onClick={onClose} style={{ flex: '0 0 100%', padding: '0.55rem', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Editar dados da ficha ─────────────────────────────────────────────

function EditFichaModal({ ficha, onClose, onSaved }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm]     = useState({ nome_paciente: ficha.nome_paciente || '', whatsapp: ficha.whatsapp || '', email: ficha.email || '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome_paciente.trim()) { showError('Informe o nome da paciente'); return; }
    setLoading(true);
    try {
      const res = await fichasService.update(ficha.token, form);
      if (res.success) {
        showSuccess('Ficha atualizada!');
        onSaved();
        onClose();
      } else {
        showError(res.error || 'Erro ao atualizar ficha');
      }
    } catch {
      showError('Erro de comunicação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.05rem', fontWeight: 600 }}><Ico e="✏" sp />Editar Dados da Ficha</h3>
        <form onSubmit={handleSubmit}>
          {[
            { name: 'nome_paciente', label: 'Nome da paciente', required: true, placeholder: 'Nome completo' },
            { name: 'whatsapp', label: 'WhatsApp', placeholder: '(00) 00000-0000', type: 'tel' },
            { name: 'email', label: 'E-mail', placeholder: 'email@exemplo.com', type: 'email' },
          ].map(f => (
            <div key={f.name} style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#5a3e2b', marginBottom: '0.3rem' }}>{emo(f.label)}{f.required && ' *'}</label>
              <input type={f.type || 'text'} required={f.required} placeholder={f.placeholder}
                value={form[f.name]} onChange={e => setForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                style={{ width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.65rem', background: '#f6f0ea', color: '#5a3e2b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
            <button type="submit" disabled={loading} style={{ flex: 1, padding: '0.65rem', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {emo(loading ? 'Salvando...' : '💾 Salvar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function FichasOnline({ onNavigate }) {
  const { showSuccess, showError } = useToast();
  const [fichas, setFichas]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('todos');
  const [viewing, setViewing]       = useState(null);
  const [editing, setEditing]       = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showNovoModal, setShowNovoModal] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [importing, setImporting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fichasService.getAll();
      if (res.success) setFichas(res.data || []);
    } catch { showError('Erro ao carregar fichas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (token) => {
    setImporting(token);
    try {
      const res = await fichasService.importar(token);
      if (res.success) {
        showSuccess('Ficha importada! Redirecionando para o prontuário...');
        setViewing(null);
        load();
        if (res.data?.patient_id) {
          setTimeout(() => onNavigate('patient-detail', res.data.patient_id), 1200);
        }
      } else showError(res.error || 'Erro ao importar');
    } catch { showError('Erro ao importar ficha'); }
    finally { setImporting(null); }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await fichasService.deletar(confirmDel);
      showSuccess('Ficha removida');
      load();
    } catch { showError('Erro ao remover'); }
    finally { setConfirmDel(null); }
  };

  const handleCopyLink = (ficha) => {
    navigator.clipboard.writeText(ficha.link);
    showSuccess('Link copiado!');
  };

  const filtered = fichas.filter(f => filter === 'todos' || f.status === filter);
  const counts = { todos: fichas.length, pendente: fichas.filter(f => f.status === 'pendente').length,
    preenchida: fichas.filter(f => f.status === 'preenchida').length, importada: fichas.filter(f => f.status === 'importada').length };

  return (
    <div style={{ maxWidth: '900px', padding: '0' }}>
      {/* Modais */}
      {showNovoModal && (
        <NovoTokenModal
          onClose={() => setShowNovoModal(false)}
          onGenerated={(data) => { setLinkResult(data); load(); }}
        />
      )}
      {linkResult && <LinkModal result={linkResult} onClose={() => setLinkResult(null)} />}
      {viewing && (
        <ViewModal
          ficha={viewing}
          onClose={() => setViewing(null)}
          onNavigate={onNavigate}
        />
      )}
      {editing && (
        <EditFichaModal
          ficha={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {confirmDel && (
        <ConfirmModal
          title="Remover ficha"
          message="Remover esta ficha permanentemente? O link ficará inválido."
          confirmLabel="Remover"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#2f2a26' }}><Ico e="📋" sp />Fichas Online</h1>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#736b64' }}>Gerencie as fichas de anamnese enviadas por link</p>
        </div>
        <button onClick={() => setShowNovoModal(true)}
          style={{ padding: '0.65rem 1.25rem', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
          + Gerar Novo Link
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {emo([
          { key: 'todos', label: `Todos (${counts.todos})` },
          { key: 'pendente', label: `⏳ Aguardando (${counts.pendente})` },
          { key: 'preenchida', label: `✅ Preenchidas (${counts.preenchida})` },
          { key: 'importada', label: `📥 Importadas (${counts.importada})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1.5px solid', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem',
              background: filter === f.key ? 'linear-gradient(135deg,#D4A574,#e8c9a8)' : '#ffffff',
              color: filter === f.key ? '#ffffff' : '#4a4440',
              borderColor: filter === f.key ? 'transparent' : '#d9d0c6' }}>
            {emo(f.label)}
          </button>
        )))}
      </div>

      {/* Alerta de fichas pendentes de importação */}
      {counts.preenchida > 0 && (
        <div style={{ background: '#edf4ee', border: '1.5px solid #46714f', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.2rem' }}><Ico e="📬" /></span>
          <p style={{ margin: 0, fontWeight: 600, color: '#2f5236', fontSize: '0.9rem' }}>
            {counts.preenchida} ficha{counts.preenchida > 1 ? 's' : ''} preenchida{counts.preenchida > 1 ? 's' : ''} aguardando importação!
          </p>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#736b64' }}>Carregando...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#d9d0c6', background: '#ffffff', borderRadius: '12px', border: '1px solid #ebe3da' }}>
          <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}><Ico e="📋" /></p>
          <p style={{ margin: 0 }}>{filter === 'todos' ? 'Nenhuma ficha gerada ainda.' : 'Nenhuma ficha com este status.'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(f => {
            const cfg = STATUS_CFG[f.status] || STATUS_CFG.pendente;
            const waPhone = (f.whatsapp || '').replace(/\D/g, '');
            return (
              <div key={f.id} style={{ background: '#ffffff', borderRadius: '12px', padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #ebe3da', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Avatar */}
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${BRAND}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: BRAND2, fontSize: '1.1rem', flexShrink: 0 }}>
                  {(f.nome_paciente || '?').charAt(0).toUpperCase()}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <p style={{ margin: '0 0 0.15rem', fontWeight: 600, fontSize: '0.95rem', color: '#2f2a26' }}>{f.nome_paciente || '—'}</p>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#736b64' }}>
                    {f.whatsapp || 'Sem WhatsApp'}
                    {f.preenchido_em ? ` · Preenchida ${fmtDate(f.preenchido_em)}` : ` · Criada ${fmtDate(f.criado_em)}`}
                  </p>
                </div>
                {/* Status badge */}
                <span style={{ padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: cfg.bg, color: cfg.color, flexShrink: 0 }}>
                  {emo(cfg.label)}
                </span>
                {/* Ações */}
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                  {f.status === 'pendente' && (
                    <>
                      <button onClick={() => handleCopyLink(f)}
                        style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="🔗" sp />Copiar Link
                      </button>
                      {waPhone && (
                        <a href={`https://wa.me/55${waPhone}`} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', fontWeight: 600, fontSize: '0.8rem', textDecoration: 'none' }}>
                          <Ico e="💬" sp />WhatsApp
                        </a>
                      )}
                      <button onClick={() => setConfirmDel(f.token)}
                        style={{ padding: '0.4rem 0.75rem', background: 'none', color: '#a8483f', border: '1px solid #e3b3ad', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="🗑" sp />Deletar
                      </button>
                    </>
                  )}
                  {f.status === 'preenchida' && (
                    <>
                      <button onClick={() => setViewing(f)}
                        style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="👁" sp />Ver
                      </button>
                      <button onClick={() => setEditing(f)}
                        style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="✏" sp />Editar
                      </button>
                      <button onClick={() => handleImport(f.token)} disabled={importing === f.token}
                        style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        {emo(importing === f.token ? '⏳...' : '➕ Importar')}
                      </button>
                      {waPhone && (
                        <a href={`https://wa.me/55${waPhone}`} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', fontWeight: 600, fontSize: '0.8rem', textDecoration: 'none' }}>
                          <Ico e="💬" sp />WhatsApp
                        </a>
                      )}
                      <button onClick={() => setConfirmDel(f.token)}
                        style={{ padding: '0.4rem 0.75rem', background: 'none', color: '#a8483f', border: '1px solid #e3b3ad', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="🗑" sp />Deletar
                      </button>
                    </>
                  )}
                  {f.status === 'importada' && (
                    <>
                      <button onClick={() => setViewing(f)}
                        style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="👁" sp />Ver
                      </button>
                      <button onClick={() => setEditing(f)}
                        style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="✏" sp />Editar
                      </button>
                      {waPhone && (
                        <a href={`https://wa.me/55${waPhone}`} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '0.4rem 0.75rem', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', fontWeight: 600, fontSize: '0.8rem', textDecoration: 'none' }}>
                          <Ico e="💬" sp />WhatsApp
                        </a>
                      )}
                      <button onClick={() => setConfirmDel(f.token)}
                        style={{ padding: '0.4rem 0.75rem', background: 'none', color: '#a8483f', border: '1px solid #e3b3ad', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Ico e="🗑" sp />Deletar
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
